/**
 * PromptLibraryPage — Story 3.14: Check-In Prompt Library Admin Management
 *
 * Admin UI for managing check-in prompt templates:
 *   - View all templates grouped by category (active and retired)
 *   - Filter by category, status (active/retired/all), and locale
 *   - Create new templates via modal form with authoring-rules checklist
 *   - Edit existing templates (with version-based optimistic concurrency)
 *   - Retire templates (with confirmation dialog)
 *
 * Requires staff:admin role. Templates take effect from next day's prompt computation.
 *
 * @NonPhiEndpoint — clinical prompt templates only; no member PHI.
 */

import { usePortalAuth } from '@nrx/auth-ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

import { SERVICES_BASE_URL } from '../../lib/services-base.js';

import { RetireConfirmDialog } from './components/retire-confirm-dialog.js';
import { TemplateFormModal } from './components/template-form-modal.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export type PromptTemplateCategory =
  | 'mood'
  | 'sleep'
  | 'meal'
  | 'energy'
  | 'resilience'
  | 'celebration'
  | 'goal_progress'
  | 'reflection';

export interface PromptTemplate {
  templateId: string;
  parentTemplateId: string | null;
  locale: string | null;
  category: PromptTemplateCategory;
  structuralTemplate: string;
  chips: string[];
  culturalTags: string[];
  clinicalTracks: string[];
  status: 'active' | 'retired';
  version: number;
  updatedBy: string;
  updatedAt: string;
}

const CATEGORY_LABELS: Record<PromptTemplateCategory, string> = {
  mood: 'Mood',
  sleep: 'Sleep',
  meal: 'Meal',
  energy: 'Energy',
  resilience: 'Resilience',
  celebration: 'Celebration',
  goal_progress: 'Goal Progress',
  reflection: 'Weekly Reflection',
};

const ALL_CATEGORIES: PromptTemplateCategory[] = [
  'mood',
  'sleep',
  'meal',
  'energy',
  'resilience',
  'celebration',
  'goal_progress',
  'reflection',
];

// ─── Component ────────────────────────────────────────────────────────────────

export function PromptLibraryPage(): JSX.Element {
  const auth = usePortalAuth();
  const accessToken = auth.user?.access_token;

  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Filters
  const [selectedCategory, setSelectedCategory] = useState<PromptTemplateCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'active' | 'retired' | 'all'>('all');
  const [localeFilter, setLocaleFilter] = useState<string>('');

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<PromptTemplate | null>(null);
  const [retireTemplate, setRetireTemplate] = useState<PromptTemplate | null>(null);

  // Inline messages
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    void fetchTemplates();
  }, [auth]);

  async function fetchTemplates(): Promise<void> {
    if (!accessToken) {
      setFetchError('Not authenticated — please sign in again.');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(
        `${SERVICES_BASE_URL}/v1/engagement/admin/v1/prompt-library/templates`,
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (!res.ok) {
        setFetchError(`Failed to load templates (${res.status})`);
        return;
      }
      const data = (await res.json()) as { items: PromptTemplate[]; totalCount: number };
      setTemplates(data.items);
    } catch {
      setFetchError('Unable to connect to API.');
    } finally {
      setLoading(false);
    }
  }

  async function handleRetire(templateId: string): Promise<void> {
    if (!accessToken) return;
    setActionError(null);
    setActionSuccess(null);
    try {
      const res = await fetch(
        `${SERVICES_BASE_URL}/v1/engagement/admin/v1/prompt-library/templates/${templateId}/retire`,
        {
          method: 'PATCH',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (!res.ok) {
        const body = (await res.json()) as { detail?: string };
        setActionError(`Retire failed (${res.status}): ${body.detail ?? 'Unknown error'}`);
        return;
      }
      const updated = (await res.json()) as PromptTemplate;
      setTemplates((prev) => prev.map((t) => (t.templateId === updated.templateId ? updated : t)));
      setActionSuccess('Template retired successfully.');
    } catch {
      setActionError('Unable to connect to API.');
    } finally {
      setRetireTemplate(null);
    }
  }

  function handleTemplatesSaved(saved: PromptTemplate, isNew: boolean): void {
    if (isNew) {
      setTemplates((prev) => [saved, ...prev]);
      setActionSuccess('Template created successfully.');
    } else {
      setTemplates((prev) => prev.map((t) => (t.templateId === saved.templateId ? saved : t)));
      setActionSuccess('Template updated successfully.');
    }
    setCreateModalOpen(false);
    setEditTemplate(null);
  }

  // Filtered view
  const filteredTemplates = templates.filter((t) => {
    if (selectedCategory !== 'all' && t.category !== selectedCategory) return false;
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (localeFilter.trim() !== '') {
      const lf = localeFilter.trim().toLowerCase();
      if (lf === 'en' || lf === 'base') {
        if (t.locale !== null) return false;
      } else {
        if (t.locale !== lf) return false;
      }
    }
    return true;
  });

  return (
    <div className="p-8 max-w-5xl">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Check-In Prompt Library</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage check-in prompt templates. Changes take effect from next day's prompt computation.
        </p>
      </div>

      {/* Compliance banner */}
      <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
        <p className="text-sm text-amber-800">
          <strong>Clinical Team notice:</strong> Templates authored here must follow the{' '}
          <em>Warm, Non-Judgmental Tone</em> guidelines — single warm question, no clinical advice,
          no food prescription, no numbers or targets, reads naturally verbatim.
        </p>
      </div>

      {/* Action feedback */}
      {actionSuccess !== null && <p className="mt-3 text-sm text-green-600">{actionSuccess}</p>}
      {actionError !== null && <p className="mt-3 text-sm text-red-600">{actionError}</p>}

      {/* Controls row */}
      <div className="mt-5 flex flex-wrap items-end gap-4">
        {/* Category tabs */}
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1 text-xs rounded-full border ${
              selectedCategory === 'all'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            All
          </button>
          {ALL_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1 text-xs rounded-full border ${
                selectedCategory === cat
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {CATEGORY_LABELS[cat]}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'active' | 'retired' | 'all')}
          className="text-sm border border-gray-300 rounded-md px-2 py-1"
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="retired">Retired only</option>
        </select>

        {/* Locale filter */}
        <input
          type="text"
          placeholder="Locale (e.g. es)"
          value={localeFilter}
          onChange={(e) => setLocaleFilter(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1 w-32"
          aria-label="Filter by locale"
        />

        {/* Add template button */}
        <button
          onClick={() => setCreateModalOpen(true)}
          className="ml-auto px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
        >
          + Add template
        </button>
      </div>

      {/* Template list */}
      {loading && <p className="mt-6 text-gray-400">Loading templates…</p>}
      {fetchError !== null && <p className="mt-6 text-red-600">{fetchError}</p>}

      {!loading && fetchError === null && (
        <div className="mt-5 space-y-2">
          {filteredTemplates.length === 0 && (
            <p className="text-sm text-gray-400">No templates match the current filters.</p>
          )}
          {filteredTemplates.map((t) => (
            <TemplateRow
              key={t.templateId}
              template={t}
              onEdit={() => setEditTemplate(t)}
              onRetire={() => setRetireTemplate(t)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {createModalOpen && (
        <TemplateFormModal
          mode="create"
          accessToken={accessToken ?? ''}
          onSaved={(t) => handleTemplatesSaved(t, true)}
          onClose={() => setCreateModalOpen(false)}
        />
      )}
      {editTemplate !== null && (
        <TemplateFormModal
          mode="edit"
          existing={editTemplate}
          accessToken={accessToken ?? ''}
          onSaved={(t) => handleTemplatesSaved(t, false)}
          onClose={() => setEditTemplate(null)}
        />
      )}
      {retireTemplate !== null && (
        <RetireConfirmDialog
          template={retireTemplate}
          onConfirm={() => void handleRetire(retireTemplate.templateId)}
          onCancel={() => setRetireTemplate(null)}
        />
      )}
    </div>
  );
}

// ─── Template Row ─────────────────────────────────────────────────────────────

interface TemplateRowProps {
  template: PromptTemplate;
  onEdit: () => void;
  onRetire: () => void;
}

function TemplateRow({ template, onEdit, onRetire }: TemplateRowProps): JSX.Element {
  const isRetired = template.status === 'retired';

  return (
    <div
      className={`p-4 rounded-lg border ${
        isRetired ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Template text (truncated at 80 chars) */}
          <p className={`text-sm font-medium ${isRetired ? 'text-gray-400' : 'text-gray-900'}`}>
            {template.structuralTemplate.length > 80
              ? template.structuralTemplate.slice(0, 80) + '…'
              : template.structuralTemplate}
          </p>

          {/* Chips */}
          <div className="mt-1 flex flex-wrap gap-1">
            {template.chips.map((chip) => (
              <span
                key={chip}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
              >
                {chip}
              </span>
            ))}
          </div>

          {/* Scope labels */}
          <div className="mt-1.5 flex flex-wrap gap-2 text-xs text-gray-400">
            <span className="uppercase tracking-wide">{CATEGORY_LABELS[template.category]}</span>
            {template.locale !== null && (
              <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded">
                locale: {template.locale}
              </span>
            )}
            {template.culturalTags.length > 0 && (
              <span className="px-1.5 py-0.5 bg-orange-50 text-orange-700 rounded">
                tags: {template.culturalTags.join(', ')}
              </span>
            )}
            {template.clinicalTracks.length > 0 && (
              <span className="px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded">
                tracks: {template.clinicalTracks.join(', ')}
              </span>
            )}
          </div>

          {/* Audit meta */}
          <p className="mt-1 text-xs text-gray-400">
            v{template.version} · {template.updatedBy} ·{' '}
            {new Date(template.updatedAt).toLocaleDateString()}
          </p>
        </div>

        {/* Actions + status badge */}
        <div className="flex items-center gap-2 shrink-0">
          {isRetired ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-500">
              Retired
            </span>
          ) : (
            <>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Active
              </span>
              <button
                onClick={onEdit}
                className="text-xs text-indigo-600 hover:text-indigo-800"
                aria-label={`Edit template ${template.templateId}`}
              >
                Edit
              </button>
              <button
                onClick={onRetire}
                className="text-xs text-red-500 hover:text-red-700"
                aria-label={`Retire template ${template.templateId}`}
              >
                Retire
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
